import { Button } from "@/components/ui/button";
import { useTheme } from "@/hooks/useTheme";
import { useTimeBasedWarmth } from "@/hooks/useTimeBasedWarmth";
import { useLanguage } from "@/contexts/LanguageContext";
import { Moon, Sun } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ThemeToggleProps {
  onGradientBackground?: boolean;
}

export function ThemeToggle({ onGradientBackground = false }: ThemeToggleProps) {
  const { theme, toggleTheme } = useTheme();
  const { timeMode, userOverride, setOverride } = useTimeBasedWarmth();
  const { t } = useLanguage();

  const handleToggle = () => {
    // If it's night time and user switches from dark to light, mark as override
    if (timeMode === "night" && theme === "dark") {
      setOverride();
    }
    toggleTheme();
  };

  // Determine tooltip text
  const getTooltipText = () => {
    if (timeMode === "night" && userOverride && theme === "light") {
      return t("theme.nightModeOverride");
    }
    if (timeMode === "night" && theme === "dark") {
      return t("theme.nightModeAuto");
    }
    if (timeMode === "evening") {
      return t("theme.eveningActive");
    }
    return theme === "light" ? t("theme.switchToDark") : t("theme.switchToLight");
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size="icon"
          variant={onGradientBackground ? "ghost-utility" : "outline"}
          onClick={handleToggle}
          className="relative h-8 w-8"
          aria-label={getTooltipText()}
        >
          {theme === "light" ? (
            <Moon className="h-3.5 w-3.5" />
          ) : (
            <Sun className="h-3.5 w-3.5" />
          )}
          
          {/* Warmth indicator dot - shows during evening/night with mode-specific colors */}
          {timeMode === "evening" && (
            <span 
              className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse shadow-sm shadow-amber-400/50"
              aria-hidden="true"
            />
          )}
          {timeMode === "night" && (
            <span 
              className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse shadow-sm shadow-indigo-400/50"
              aria-hidden="true"
            />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {getTooltipText()}
      </TooltipContent>
    </Tooltip>
  );
}
